import React, { useState, useEffect } from "react";
import { Container } from "./styles";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  GoProject,
  GoFile,
  GoKey,
  GoOrganization,
  GoPlus,
  GoTrash,
  GoPencil,
  GoPackage,
} from "react-icons/go";
import { HiOutlineDocumentText } from "react-icons/hi";
import { FiSearch, FiEye, FiUsers } from "react-icons/fi";
import NewProjectModal from "../../components/NewProjectModal";
import EditProjectModal from "../../components/EditProjectModal";
import { useAuth } from "../../contexts/AuthContext";

const formatNumber = (num) => {
  if (!num) return "0";
  return num.toLocaleString("pt-BR");
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR");
};

export default function Projects() {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [aggregatedStats, setAggregatedStats] = useState({
    totalProjects: 0,
    totalSpeds: 0,
    totalXmls: 0,
    totalCertificates: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const { user } = useAuth();
  const isGod = user?.cargo === 'god';

  // Fetch projects and stats
  const fetchProjectsStats = async () => {
    setLoading(true);
    try {
      const response = await api.get("/projects/stats");
      
      if (response.data.success) {
        setProjects(response.data.projects || []);
        setAggregatedStats(response.data.aggregated || {
          totalProjects: 0,
          totalSpeds: 0,
          totalXmls: 0,
          totalCertificates: 0,
        });
      }
    } catch (error) {
      console.error("Erro ao buscar estatísticas dos projetos:", error);
      toast.error("Erro ao carregar projetos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectsStats();
  }, []);

  // Filter + sort projects (por volume de XMLs, decrescente)
  const filteredProjects = projects
    .filter((project) => {
      const matchSearch =
        project.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.cnpj?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchSearch;
    })
    .sort((a, b) => (b.stats?.xmls || 0) - (a.stats?.xmls || 0));

  // Get color without background
  const getColorStyle = (color) => {
    if (!color) return { color: "#10b981" };
    return {
      color: color,
    };
  };

  // Handle create new project
  const handleCreateProject = async (projectData) => {
    const loadingToast = toast.loading("Criando projeto...");

    try {
      const response = await api.post("/projects", {
        nome: projectData.nome,
        cnpj: projectData.cnpj,
        cidade: projectData.cidade,
        uf: projectData.uf,
        selectedUsers: projectData.selectedUsers || [],
        permissions: projectData.permissions || {},
      });

      toast.update(loadingToast, {
        render: "Projeto criado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      // Refresh projects list
      await fetchProjectsStats();
    } catch (error) {
      console.error("Erro ao criar projeto:", error);
      toast.update(loadingToast, {
        render: error.response?.data?.error || "Erro ao criar projeto",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowEditProjectModal(true);
  };

  const handleCopyProjectId = async (projectId) => {
    try {
      await navigator.clipboard.writeText(projectId);
      toast.success("ID do projeto copiado!");
    } catch (error) {
      console.error("Erro ao copiar ID:", error);
      toast.error("Erro ao copiar ID");
    }
  };

  const handleSaveProject = async () => {
    await fetchProjectsStats();
  };

  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-details">
          <h3>Gerenciamento de Projetos</h3>
          <div className="header-meta">
            <span className="status-tag">SISTEMA ATIVO</span>
            <span className="update-tag">
              {projects.length > 0
                ? `${projects.length} projeto${projects.length !== 1 ? 's' : ''} cadastrado${projects.length !== 1 ? 's' : ''}`
                : 'nenhum projeto cadastrado'}
            </span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="kpi-cards">
        <div className="kpi-card primary">
          <div className="card-content">
            <div className="thumb"><GoProject /></div>
            <div className="text">
              <p>{aggregatedStats.totalProjects}</p>
              <p>Total de Projetos</p>
            </div>
          </div>
        </div>

        <div className="kpi-card info">
          <div className="card-content">
            <div className="thumb"><GoFile /></div>
            <div className="text">
              <p>{formatNumber(aggregatedStats.totalSpeds)}</p>
              <p>SPEDs Processados</p>
            </div>
          </div>
        </div>

        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb"><HiOutlineDocumentText /></div>
            <div className="text">
              <p>{formatNumber(aggregatedStats.totalXmls)}</p>
              <p>XMLs Processados</p>
            </div>
          </div>
        </div>

        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><GoKey /></div>
            <div className="text">
              <p>{aggregatedStats.totalCertificates}</p>
              <p>Certificados Digitais</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <div className="search-input">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar projetos por nome ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="toolbar-right">
          <button className="btn-new-project" onClick={() => setShowNewProjectModal(true)}>
            <GoPlus />
            Novo Projeto
          </button>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="scrollable-content">
        {/* Projects Grid */}
        {loading ? (
          <div className="loading-container">
            <p>Carregando projetos...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="empty-state">
            <GoProject />
            <h4>Nenhum projeto encontrado</h4>
            <p>
              {searchTerm
                ? "Tente ajustar os filtros de busca"
                : "Crie um novo projeto para começar"}
            </p>
          </div>
        ) : (
          <div className="projects-grid">
            {filteredProjects.map((project) => (
              <div key={project._id} className="project-card">
                {/* Edit Button */}
                <button 
                  className="btn-edit-icon"
                  onClick={() => handleEditProject(project)}
                  title="Editar projeto"
                >
                  <GoPencil />
                </button>

                {/* Header */}
                <div className="project-card-header">
                  <div className="project-avatar">
                    <div
                      className="avatar-circle"
                      style={{
                        background: project.color || '#10b981'
                      }}
                    >
                      <GoPackage />
                    </div>
                  </div>
                  <div className="project-info">
                    <h5>{project.nome}</h5>
                    <p className="project-cnpj">{project.cnpj}</p>
                    {isGod && (
                      <p
                        className="project-id"
                        title="Clique para copiar o ID"
                        onClick={() => handleCopyProjectId(project._id)}
                      >
                        {project._id}
                      </p>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="project-card-body">
                  <div className="project-meta">
                    <div className="meta-item">
                      <GoFile />
                      <div className="meta-content">
                        <span className="meta-label">SPEDs</span>
                        <span className="meta-value">{formatNumber(project.stats?.speds || 0)}</span>
                      </div>
                    </div>
                    <div className="meta-item">
                      <HiOutlineDocumentText />
                      <div className="meta-content">
                        <span className="meta-label">XMLs</span>
                        <span className="meta-value">{formatNumber(project.stats?.xmls || 0)}</span>
                      </div>
                    </div>
                    <div className="meta-item">
                      <GoKey />
                      <div className="meta-content">
                        <span className="meta-label">Certs</span>
                        <span className="meta-value">{project.stats?.certificates || 0}</span>
                      </div>
                    </div>
                    <div className="meta-item">
                      <FiUsers />
                      <div className="meta-content">
                        <span className="meta-label">Users</span>
                        <span className="meta-value">{project.stats?.users || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Project Modal */}
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onConfirm={handleCreateProject}
      />

      {/* Edit Project Modal */}
      <EditProjectModal
        isOpen={showEditProjectModal}
        onClose={() => setShowEditProjectModal(false)}
        project={selectedProject}
        onSave={handleSaveProject}
      />
    </Container>
  );
}
